import {
    InfoOutlined,
    EditOutlined,
    DeleteOutlineOutlined,
} from '@mui/icons-material'
import {
    GridActionsCellItem,
    GridActionsCellItemProps,
    GridColDef,
    GridValidRowModel,
} from '@mui/x-data-grid'
import { LinkProps } from '@tanstack/react-router'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { GridActionsCellItemLink } from '@/components/GridActionsCellItemLink'

const seleniumPrefix = 'use-create-cud-column'

/**
 * Creates the *create*, *update* and *delete* row actions for `DataGrid`.
 */
export const useCreateCUDColumn = <T extends GridValidRowModel>({
    editLinkProps,
    viewLinkProps,
    deleteProps,
}: {
    viewLinkProps?: (row: T) => Pick<LinkProps, 'to' | 'params' | 'search'>
    editLinkProps?: (
        row: T
    ) => Pick<LinkProps, 'to' | 'params' | 'search' | 'disabled'>
    deleteProps?: (row: T) => Pick<GridActionsCellItemProps, 'disabled'> & {
        onClick?: React.MouseEventHandler
    }
} = {}): GridColDef<T> => {
    const { t } = useTranslation()

    return React.useMemo(
        () => ({
            field: 'actions',
            headerName: '',
            width: 125,
            minWidth: 125,
            getActions: ({ row }) => [
                <GridActionsCellItemLink
                    key={'info'}
                    icon={<InfoOutlined />}
                    label={t('common.view')}
                    showInMenu={false}
                    {...viewLinkProps?.(row)}
                    data-selenium={`${seleniumPrefix}-view`}
                />,
                <GridActionsCellItemLink
                    key={'edit'}
                    icon={<EditOutlined />}
                    label={t('common.edit')}
                    showInMenu={false}
                    {...editLinkProps?.(row)}
                    data-selenium={`${seleniumPrefix}-edit`}
                />,
                <GridActionsCellItem
                    key={'delete'}
                    icon={<DeleteOutlineOutlined />}
                    label={t('common.delete')}
                    showInMenu={false}
                    {...deleteProps?.(row)}
                    data-selenium={`${seleniumPrefix}-delete`}
                />,
            ],
            type: 'actions',
            sortable: false,
            filterable: false,
            hideable: false,
            resizable: false,
        }),
        [deleteProps, editLinkProps, t, viewLinkProps]
    )
}
